

import React, { type ErrorInfo } from "react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class EnhancedErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(
    error: Error,
    errorInfo: ErrorInfo
  ): void {
    this.setState({ errorInfo })
    console.error(
      "EnhancedErrorBoundary caught an error:",
      error,
      errorInfo
    )
  }

  render() {
    const isDev = import.meta.env.MODE === "development"

    if (this.state.hasError) {
      return (
        <Card className="mx-auto mt-8 w-full max-w-3xl border-red-500">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-red-600">
              Ops! Algo deu errado
            </CardTitle>
            <CardDescription>
              Houve um problema ao carregar a página. Por favor, tente
              atualizar ou volte para a página inicial.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isDev && this.state.errorInfo && (
              <div className="mt-4">
                <h4 className="mb-2 text-lg font-semibold">
                  Component Stack
                </h4>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-gray-100 p-4 text-sm">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
            <div className="mt-6 flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/")}
              >
                Voltar ao Início
              </Button>
              <Button
                onClick={() => window.location.reload()}
              >
                Atualizar Página
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    return this.props.children
  }
}

export default EnhancedErrorBoundary
